import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { ArrowLeft, Check, Loader2, MessageCircleQuestion, Printer, Sparkles } from "lucide-react";
import { toast } from "sonner";

import {
  GET_SITUATION,
  GET_SITUATION_FOR_GROWNUP,
} from "@/graphql/queries/situations";
import {
  MARK_SITUATION_ANSWER,
  SUBMIT_SITUATION_ANSWERS,
} from "@/graphql/mutations/situations";
import {
  AnswerField,
  answerKeysOf,
  counterKey,
  reasonKey,
  type SituationQuestion,
} from "@/components/situations/answer-fields";
import { SituationEditor } from "@/components/situations/situation-editor";
import { SituationStory } from "@/components/situations/situation-story";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth/AuthContext";

type Question = SituationQuestion & {
  mark?: string | null;
};

const GOOD = "good";
const PRACTICE = "practice";

/**
 * One situation: the story, and a box under each question.
 *
 * There is no checking here and no right answer to check against — writing
 * something IS the answer. So the page has no "teisingai/neteisingai" anywhere,
 * no score, and the save button says „Išsaugoti", not „Tikrinti".
 *
 * A grown-up reading the same page gets two extra things a learner never sees:
 * the discussion notes, and a mark per answer. The mark is a note about what to
 * talk about next, not a grade — nothing averages it and the child is not shown
 * it.
 */
export default function SituationPage() {
  const { key = "" } = useParams();
  const [params] = useSearchParams();
  const { student: me, isGuardian } = useAuth();

  const studentId = isGuardian ? params.get("student") : me?.id;
  // Which session this sheet is being read as part of, if any. It decides whose
  // wording is served — the session's rewrite, or the file's. A session that does
  // not hold this problem is ignored server-side rather than refused, so a stale
  // link is a readable sheet instead of an error in front of a child.
  const sessionId = params.get("session");

  const { data, loading } = useQuery(
    isGuardian ? GET_SITUATION_FOR_GROWNUP : GET_SITUATION,
    {
      variables: { studentId, key, sessionId },
      skip: !studentId || !key,
      fetchPolicy: "cache-and-network",
    }
  );

  const situation = (data as any)?.situation;
  const questions: Question[] = useMemo(() => situation?.questions ?? [], [situation]);

  const [submit, { loading: saving }] = useMutation(SUBMIT_SITUATION_ANSWERS);
  const [mark] = useMutation(MARK_SITUATION_ANSWER);

  // Local drafts, seeded from the server once the sheet arrives. Kept in state
  // rather than written on every keystroke: this is writing, not answering, and
  // a request per character would be both wasteful and distracting.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || questions.length === 0) return;
    const initial: Record<string, string> = {};
    questions.forEach((q) => {
      initial[q.key] = q.answer ?? "";
      // A „Kodėl?" box and an answer to the peer's argument are each a second
      // thing the child wrote, stored under their own keys — so each is its own
      // draft. See Problems::REASON_SUFFIX and ::COUNTER_SUFFIX.
      if (q.asksWhy) initial[reasonKey(q.key)] = q.reasonAnswer ?? "";
      if (q.counter) initial[counterKey(q.key)] = q.counterAnswer ?? "";
    });
    setDrafts(initial);
    setSeeded(true);
  }, [questions, seeded]);

  const stored = (question: Question, key: string) => {
    if (key === question.key) return question.answer ?? "";
    if (key === reasonKey(question.key)) return question.reasonAnswer ?? "";
    return question.counterAnswer ?? "";
  };

  const dirty = questions.some((q) =>
    answerKeysOf(q).some((key) => (drafts[key] ?? "") !== stored(q, key))
  );
  // What is written, not what is right — and a tap counts as written, because
  // on a closed question it is the whole answer.
  const written = questions.filter((q) => (drafts[q.key] ?? "").trim().length > 0).length;

  const save = async () => {
    if (!studentId) return;

    const answers = questions.flatMap((q) =>
      answerKeysOf(q).map((key) => ({ questionKey: key, text: drafts[key] ?? "" }))
    );

    try {
      const result: any = await submit({
        variables: { studentId, situationKey: key, answers },
      });
      const errors = result?.data?.submitSituationAnswers?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      toast.success("Išsaugota");
    } catch (error) {
      toast.error("Nepavyko išsaugoti");
      console.error(error);
    }
  };

  const setMark = async (questionKey: string, next: string | null) => {
    if (!studentId) return;
    try {
      await mark({
        variables: { studentId, situationKey: key, questionKey, mark: next },
      });
    } catch (error) {
      toast.error("Nepavyko pažymėti");
      console.error(error);
    }
  };

  if (loading && !situation) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!situation) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center text-muted-foreground">
        Situacija nerasta.
      </div>
    );
  }

  // Back where they came from: the session when this sheet belongs to one, the
  // catalogue otherwise. A child sent from a session to the shelves would have to
  // find their way back into it.
  const backTo = sessionId
    ? `/lab/session/${sessionId}${studentId && isGuardian ? `?student=${studentId}` : ""}`
    : `/lab${studentId && isGuardian ? `?student=${studentId}` : ""}`;

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-24">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to={backTo}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-bold tracking-tight">
            {situation.title}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {/* No school year anywhere on this page, for anybody. The year
                decides which wording a learner is handed and nothing else — it
                is not a property of the problem, and printing it invites the
                reading the whole section exists to avoid. */}
            {situation.themeLabels.map((label: string) => (
              <Badge key={label} variant="outline" className="font-normal">
                {label}
              </Badge>
            ))}
          </div>
        </div>
        {isGuardian && studentId && (
          <div className="flex shrink-0 items-center gap-2">
            {/* No „Pritaikyti" here: rewording belongs to a session now, and a
                situation read outside one is read as the file wrote it. The
                session page is where that button lives. */}
            <Button variant="outline" asChild>
              <Link to={`/lab/${key}/print?student=${studentId}${sessionId ? `&session=${sessionId}` : ""}`}>
                <Printer className="mr-2 h-4 w-4" />
                Spausdinti
              </Link>
            </Button>
          </div>
        )}
      </div>

      {situation.intro && (
        <p className="text-muted-foreground">{situation.intro}</p>
      )}

      {/* Read what the file says, type your own, or throw the rewrite away. All
          three touch the same row; none of them touches the learner's answers.
          Only inside a session — that is where a rewrite is stored, so there is
          nowhere to save an edit made outside one. */}
      {isGuardian && sessionId && (
        <SituationEditor sessionId={sessionId} situation={situation} />
      )}

      <Card className={situation.personalized ? "border-primary/40" : undefined}>
        <CardContent className="py-6">
          {/* Said on the story itself, not only in the dialog: a grown-up
              reading this needs to know they are not looking at the file. */}
          {isGuardian && situation.personalized && (
            <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" />
              Pritaikyta šiam mokiniui
              {situation.personalizationTheme ? ` · ${situation.personalizationTheme}` : ""}
            </p>
          )}
          <SituationStory html={situation.story} />
        </CardContent>
      </Card>

      {/* Guardian-only, and the server enforces it: this names what the
          situation is testing, which is the child's own work to do. */}
      {isGuardian && situation.forGrownup && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
              <MessageCircleQuestion className="h-4 w-4" />
              Suaugusiajam
            </div>
            <SituationStory
              html={situation.forGrownup}
              className="text-[0.95rem] [&_li]:ml-4 [&_li]:list-disc [&_ul]:space-y-1"
            />
          </CardContent>
        </Card>
      )}

      <div className="space-y-5">
        {questions.map((question, index) => (
          <Card key={question.key}>
            <CardContent className="space-y-3 py-5">
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold">
                  {index + 1}
                </span>
                <p className="font-medium leading-relaxed">{question.prompt}</p>
              </div>

              <AnswerField
                question={question}
                value={drafts[question.key] ?? ""}
                reason={drafts[reasonKey(question.key)] ?? ""}
                counterAnswer={drafts[counterKey(question.key)] ?? ""}
                onChange={(next) =>
                  setDrafts((prev) => ({ ...prev, [question.key]: next }))
                }
                onReasonChange={(next) =>
                  setDrafts((prev) => ({ ...prev, [reasonKey(question.key)]: next }))
                }
                onCounterChange={(next) =>
                  setDrafts((prev) => ({ ...prev, [counterKey(question.key)]: next }))
                }
              />

              {isGuardian && (
                <MarkRow
                  mark={question.mark ?? null}
                  onChange={(next) => setMark(question.key, next)}
                />
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* The bar states what is written, not what is right. */}
      <div className="sticky bottom-4 flex items-center gap-3 rounded-lg border bg-background/95 p-3 shadow-lg backdrop-blur">
        <span className="text-sm text-muted-foreground">
          Parašyta {written} iš {questions.length}
        </span>
        <div className="flex-1" />
        {/* The way back into the set, on the bar where they just saved. Nothing
            navigates on its own: a sheet they may want to re-read is not
            something to whisk away from under them. */}
        {sessionId && (
          <Button variant="ghost" asChild>
            <Link to={backTo}>Į sesiją</Link>
          </Button>
        )}
        <Button onClick={save} disabled={saving || !dirty}>
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Išsaugoti
        </Button>
      </div>
    </div>
  );
}

/**
 * The grown-up's two buttons. Pressing the active one again clears the mark,
 * which is the ordinary state and reads as accepted — a sheet nobody has marked
 * is not a queue of pending work.
 */
function MarkRow({
  mark,
  onChange,
}: {
  mark: string | null;
  onChange: (next: string | null) => void;
}) {
  return (
    <div className="flex items-center gap-2 border-t pt-3">
      <span className="text-sm text-muted-foreground">Tėvų žymė:</span>
      <Button
        type="button"
        size="sm"
        variant={mark === GOOD ? "default" : "outline"}
        onClick={() => onChange(mark === GOOD ? null : GOOD)}
      >
        Gerai
      </Button>
      <Button
        type="button"
        size="sm"
        variant={mark === PRACTICE ? "default" : "outline"}
        onClick={() => onChange(mark === PRACTICE ? null : PRACTICE)}
      >
        Reikia padirbėti
      </Button>
    </div>
  );
}
